import useTitle from '@/hooks/useTitle';
import files from '@/services/FileService';
import useAppStore from '@/store/app';
import useSearchStore from '@/store/search';
import {
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DownloadIcon from '@mui/icons-material/Download';
import { useEffect } from 'react';
import useApiDelete from '@/hooks/useApiDelete';
import useApiQuery from '@/hooks/useApiQuery';
import useLiveData from '@/hooks/useLiveData';
import { Delete, Add } from '@mui/icons-material';
import ListView from '@/components/datagrid/ListView';
import { Box } from '@mui/system';
import InvoiceUploadDialog from './InvoiceUploadDialog';
import useApiMutation from '@/hooks/useApiMutation';
import { formatBytes } from '@/utils/formatBytes';
import { useRouteLoaderData } from 'react-router-dom';
import { Check } from '@mui/icons-material';
import ToastMessage from '@/components/toasts/ToastMessage';

export default function ContractsInvoicesPage() {
  useTitle(`Eingangsrechnungen`);
  const {
    me,
    fixedDeploymentConfigs: { contracts },
  } = useRouteLoaderData('auth');

  const [selectedRows, setSelectedRows] = useState([]);
  const { selectedItem, setSelectedItem } = useAppStore();
  const [downloading, setDownloading] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('alle');
  const [workflowRunning, setWorkflowRunning] = useState(false);
  const [workflowStartTime, setWorkflowStartTime] = useState(null);
  const [workflowElapsed, setWorkflowElapsed] = useState('00:00');
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      key: 'contractInvoices',
      label: 'Eingangs-RE',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });

    return () => {
      unregisterLocalSearch();
    };
  }, [registerLocalSearch, unregisterLocalSearch]);

  useEffect(() => {
    let interval;
    if (workflowRunning && workflowStartTime) {
      interval = setInterval(() => {
        const elapsed = Date.now() - workflowStartTime;
        const minutes = Math.floor(elapsed / 60000);
        const seconds = Math.floor((elapsed % 60000) / 1000);
        setWorkflowElapsed(
          `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
        );
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [workflowRunning, workflowStartTime]);

  const mongoFilter = useMemo(() => {
    const filter = {
      $and: [
        {
          filename: {
            $regex: `^msp/${filterType === 'alle' ? '' : `${filterType}/`}`,
            $options: 'i',
          },
        },
      ],
    };
    if (searchQuery) {
      filter.$and.push({ filename: { $regex: searchQuery, $options: 'i' } });
    }
    return filter;
  }, [searchQuery, filterType]);

  const { mutateAsync: deleteFile } = useApiDelete('file');
  const { mutateAsync: createFile } = useApiMutation('file');
  const { mutateAsync: updatedFile } = useApiMutation();

  const {
    data: filesArr,
    isPending,
    refetch,
  } = useApiQuery('file', mongoFilter, true);
  const { livedata } = useLiveData('file');

  const handleDownload = async (file) => {
    setDownloading((prev) => ({ ...prev, [file._id]: true }));
    try {
      const fileResponse = await files.get(`/${file._id}`, {
        responseType: 'blob',
      });
      if (!fileResponse?.data) {
        throw new Error('Datei nicht vorhanden!');
      }
      const blob = new Blob([fileResponse.data], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${file.filename}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fehler beim Herunterladen der Datei');
    } finally {
      setDownloading((prev) => ({ ...prev, [file._id]: false }));
    }
  };

  const handleDelete = () => {
    selectedRows.forEach((id) => {
      if (selectedItem === id) {
        setSelectedItem(null);
      }
      deleteFile(id);
    });
    setSelectedRows([]);
  };

  const handleUploadFiles = async (itemsToUpload) => {
    try {
      const uploadPromises = itemsToUpload.map(async (item) => {
        const ext = item.file.name.split('.').pop();
        let finalFilename;
        if (item.isConfig) {
          finalFilename = `msp/config/${item.templateName}.${ext}`;
        } else {
          const monthStr = String(item.month).padStart(2, '0');
          finalFilename = `msp/${monthStr}-${item.year}/${item.templateName}.${ext}`;
        }
        const existingFile = filesArr?.find(
          (f) => f.filename === finalFilename,
        );

        let fileId;

        if (!existingFile) {
          const fileRes = await createFile(
            {
              filename: finalFilename,
              size: item.size,
              public: true,
              sharedWith: [...(contracts?.contractShares || [])],
            },
            undefined,
            undefined,
            false,
          );
          if (fileRes.acknowledged) {
            fileId = fileRes.insertedId;
          }
        } else {
          await updatedFile(
            {
              _id: existingFile._id,
              size: item.size,
              public: true,
              sharedWith: [...(contracts?.contractShares || [])],
            },
            'file',
            existingFile._id,
          );
          fileId = existingFile._id;
        }

        if (fileId) {
          const formData = new FormData();
          formData.append('file', item.file);
          await files.post(`/${fileId}`, formData);
        }
      });

      await Promise.all(uploadPromises);
      toast.success(
        `${itemsToUpload.length} Dateien erfolgreich hochgeladen/aktualisiert.`,
      );
      refetch();
    } catch (error) {
      console.error(error);
      toast.error('Fehler beim Hochladen der Dateien.');
      throw error;
    }
  };

  const handleWorkflowTrigger = async () => {
    if (!contracts?.workflowTriggerUrl || workflowRunning) return;

    setWorkflowRunning(true);
    const startTime = Date.now();
    setWorkflowStartTime(startTime);
    setWorkflowElapsed('00:00');

    const toastId = toast.info(
      <ToastMessage
        title="Workflow wird ausgeführt..."
        subtitle="Laufzeit: 00:00"
      />,
      {
        position: 'bottom-right',
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
      },
    );

    const toastUpdateInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);
      const elapsedString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      toast.update(toastId, {
        render: (
          <ToastMessage
            title="Workflow wird ausgeführt..."
            subtitle={`Laufzeit: ${elapsedString}`}
          />
        ),
      });
    }, 1000);

    try {
      const response = await fetch(contracts.workflowTriggerUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const finalElapsed = Date.now() - startTime;
      const finalMinutes = Math.floor(finalElapsed / 60000);
      const finalSeconds = Math.floor((finalElapsed % 60000) / 1000);
      const finalElapsedString = `${finalMinutes.toString().padStart(2, '0')}:${finalSeconds.toString().padStart(2, '0')}`;

      clearInterval(toastUpdateInterval);
      toast.dismiss(toastId);
      toast.success(
        <ToastMessage
          title="Workflow erfolgreich abgeschlossen"
          subtitle={`Ausführungszeit: ${finalElapsedString}`}
        />,
      );
    } catch (error) {
      console.error('Workflow error:', error);

      const errorElapsed = Date.now() - startTime;
      const errorMinutes = Math.floor(errorElapsed / 60000);
      const errorSeconds = Math.floor((errorElapsed % 60000) / 1000);
      const errorElapsedString = `${errorMinutes.toString().padStart(2, '0')}:${errorSeconds.toString().padStart(2, '0')}`;

      clearInterval(toastUpdateInterval);
      toast.dismiss(toastId);

      toast.error(
        <ToastMessage
          title="Workflow fehlgeschlagen"
          subtitle={`Fehler nach ${errorElapsedString}. Eine E-Mail wurde versendet.`}
        />,
      );
    } finally {
      setWorkflowRunning(false);
      setWorkflowStartTime(null);
      setWorkflowElapsed('00:00');
    }
  };

  const mappedRows = useMemo(() => {
    if (!filesArr) return [];
    return filesArr.map((file) => {
      let invoiceMonth = '';
      let invoiceYear = '';
      let invoiceType = '';
      let period = '';
      let isConfig = false;

      if (file.filename) {
        const parts = file.filename.split('/');
        if (parts[1] === 'config') {
          isConfig = true;
          invoiceType = parts[2]?.split('.')[0] || '';
        } else {
          const dateParts = parts[1].split('-');
          if (dateParts.length === 2) {
            invoiceMonth = dateParts[0];
            invoiceYear = dateParts[1];
          }
          if (parts.length === 3) {
            invoiceType = parts[2].split('.')[0];
          }
        }
      }

      const template = contracts?.templates?.find(
        (t) => t.name === invoiceType,
      );
      if (template && !isConfig) {
        period = template.period;
      }

      return {
        ...file,
        invoiceMonth,
        invoiceYear,
        invoiceType,
        period,
        isConfig,
        displayName: invoiceType,
      };
    });
  }, [filesArr, contracts.templates]);

  const columns = [
    {
      field: 'invoiceType',
      headerName: 'Typ',
      flex: 1,
      valueGetter: (params) => params || 'Unbekannt',
    },
    {
      field: 'isConfig',
      headerName: 'Konfig',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.isConfig ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Check />
          </Box>
        ) : (
          ''
        ),
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'period',
      headerName: 'Intervall',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (params.row.isConfig ? '' : params.value),
    },
    {
      field: 'invoiceMonth',
      headerName: 'Monat',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (params.row.isConfig ? '' : params.value),
    },
    {
      field: 'invoiceYear',
      headerName: 'Jahr',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (params.row.isConfig ? '' : params.value),
    },
    {
      field: 'size',
      headerName: 'Größe',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatBytes(params ?? 0),
    },
    {
      field: 'createdAt',
      headerName: 'Datum',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    },
    {
      field: 'download',
      headerName: '',
      align: 'center',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      width: 70,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(params.row);
          }}
          disabled={downloading[params.row._id]}
        >
          {downloading[params.row._id] ? (
            <CircularProgress
              size={24}
              color="secondary"
              sx={{ position: 'absolute' }}
            />
          ) : (
            <DownloadIcon />
          )}
        </IconButton>
      ),
    },
  ];

  const actions = [
    {
      title: 'Hinzufügen',
      label: 'Hinzufügen',
      color: 'primary',
      icon: Add,
      onClick: () => setIsDialogOpen(true),
    },
  ];
  if (contracts?.workflowTriggerUrl) {
    actions.push({
      title: 'Workflow starten',
      label: workflowRunning
        ? `Workflow läuft... (${workflowElapsed})`
        : 'Workflow starten',
      color: 'primary',
      onClick: handleWorkflowTrigger,
      disabled: workflowRunning,
    });
  }
  actions.push(
    <Box sx={{ flexGrow: 1 }} key="spacer" />,
    <ToggleButtonGroup
      key="toggle-group"
      value={filterType}
      exclusive
      onChange={(_e, val) => val && setFilterType(val)}
      size="small"
      sx={{ mr: 2 }}
      color="primary"
    >
      <ToggleButton value="alle">Alle</ToggleButton>
      <ToggleButton value="config">Konfiguration</ToggleButton>
    </ToggleButtonGroup>,
    {
      title: 'Löschen',
      label: 'Löschen',
      color: 'error',
      selection: 'multi',
      icon: Delete,
      onClick: handleDelete,
    },
  );

  return (
    <>
      <ListView
        key={JSON.stringify(mongoFilter)}
        rows={mappedRows}
        columns={columns}
        actions={actions}
        loading={isPending}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        livedata={livedata}
        refetch={refetch}
      />
      <InvoiceUploadDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onUpload={handleUploadFiles}
        config={contracts}
        existingFiles={filesArr || []}
      />
    </>
  );
}
